class CartRemoveItem extends HTMLElement {
  connectedCallback() {
    if (this.boundClick) return;
    this.boundClick = (event) => {
      if (!event.target.closest('a')) return;
      event.preventDefault();
      this.closest('cart-items')?.updateQuantity(this.dataset.index, 0);
    };
    this.addEventListener('click', this.boundClick);
  }
}
customElements.define('cart-remove', CartRemoveItem);

class CartItemsUpdate extends HTMLElement {
  static updating = false;

  connectedCallback() {
    this.changeHandler = (event) => {
      const input = event.target;
      if (!input.matches('input[name="updates[]"][data-index]')) return;
      clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => {
        if (input.isConnected) this.updateQuantity(input.dataset.index, input.value, input.name);
      }, ON_CHANGE_DEBOUNCE_TIMER);
    };
    this.addEventListener('change', this.changeHandler);
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      if (event.source === 'product-form' && this.closest('cart-drawer')) return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.changeTimer);
    this.removeEventListener('change', this.changeHandler);
    this.cartUpdateUnsubscriber?.();
    this.refreshController?.abort();
  }

  async onCartUpdate() {
    this.refreshController?.abort();
    this.refreshController = new AbortController();
    const controller = this.refreshController;
    const sections = this.getSectionsToRender();
    const ids = [...new Set(sections.map((section) => section.section))];
    if (!ids.length) return;
    try {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url.pathname + url.search, { signal: controller.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      const markup = await response.json();
      if (controller.signal.aborted) return;
      this.renderSections(markup, sections);
      document.querySelector('cart-drawer.open')?.restoreFocusAfterUpdate?.();
    } catch (error) {
      if (error.name !== 'AbortError') this.showError('No pudimos actualizar el carrito. Recarga la página para ver su estado actual.');
    }
  }

  getSectionsToRender() {
    const definitions = [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '.drawer-inner-card' },
      { id: 'main-cart', selector: '#main-cart' },
      { id: 'main-cart-summary', selector: '.main-summary-content' },
    ];
    return definitions.flatMap((definition) => {
      const root = document.getElementById(definition.id);
      if (!root) return [];
      const target = root.matches(definition.selector) ? root : root.querySelector(definition.selector);
      const section = definition.section || root.dataset.id;
      return target && section ? [{ ...definition, section, target }] : [];
    });
  }

  renderSections(markup, definitions = this.getSectionsToRender()) {
    const updates = definitions.map((definition) => {
      const html = markup?.[definition.section];
      if (typeof html !== 'string') throw new Error('Missing cart section');
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(definition.selector);
      if (!source) throw new Error('Missing cart content');
      return { definition, source };
    });
    updates.forEach(({ definition, source }) => {
      if (definition.target.isConnected) definition.target.innerHTML = source.innerHTML;
    });
  }

  setBusy(busy) {
    document.querySelectorAll('cart-items input[name="updates[]"], cart-items quantity-input button, button[name="checkout"], input[name="checkout"]').forEach((control) => {
      if (busy) {
        control.dataset.vrCartDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
      } else if (control.dataset.vrCartDisabled !== undefined) {
        control.disabled = control.dataset.vrCartDisabled === 'true';
        delete control.dataset.vrCartDisabled;
      }
    });
    document.querySelectorAll('cart-items').forEach((element) => element.setAttribute('aria-busy', String(busy)));
  }

  async updateQuantity(line, quantity, name) {
    if (CartItemsUpdate.updating) return;
    const quantityElement = document.getElementById(`cart-quantity-${line}`) || document.getElementById(`drawer-quantity-${line}`);
    const numericQuantity = Number(quantity);
    if (!Number.isInteger(numericQuantity) || numericQuantity < 0 || String(quantity).trim() === '' || (numericQuantity !== 0 && quantityElement && !quantityElement.checkValidity())) {
      this.showError('Escribe una cantidad válida.', line);
      if (quantityElement) quantityElement.value = quantityElement.defaultValue;
      return;
    }
    const definitions = this.getSectionsToRender();
    const sectionIds = [...new Set(definitions.map((section) => section.section))];
    CartItemsUpdate.updating = true;
    this.setBusy(true);
    this.showError('', line);
    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ line: Number(line), quantity: numericQuantity, sections: sectionIds, sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status >= 400) {
        const rawMessage = typeof state.errors === 'string' ? state.errors : state.description || '';
        const message = /stock|sold out|agotad|available|disponib|inventory/i.test(rawMessage)
          ? 'No hay suficientes unidades disponibles para esta cantidad. Elige una cantidad menor.'
          : 'No pudimos cambiar esta cantidad. Revisa la disponibilidad e inténtalo nuevamente.';
        if (quantityElement) quantityElement.value = quantityElement.defaultValue;
        this.showError(message, line);
        return;
      }
      try {
        this.renderSections(state.sections, definitions);
      } catch (error) {
        window.location.assign(routes.cart_url);
        return;
      }
      document.querySelectorAll('[data-item-count]').forEach((counter) => {
        counter.textContent = state.item_count > 99 ? '99+' : String(state.item_count);
        counter.classList.toggle('hidden', state.item_count === 0);
      });
      document.querySelectorAll('[data-cart-drawer-btn]').forEach((link) => link.setAttribute('aria-label', `Carrito (${state.item_count})`));
      shippingBarProgress(state.total_price);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      if (document.querySelector('cart-drawer.open')) document.querySelector('cart-drawer').restoreFocusAfterUpdate?.(name);
    } catch (error) {
      if (quantityElement?.isConnected) quantityElement.value = quantityElement.defaultValue;
      this.showError('No pudimos confirmar el cambio. Revisa tu carrito antes de intentarlo nuevamente.', line);
    } finally {
      CartItemsUpdate.updating = false;
      this.setBusy(false);
    }
  }

  showError(message, line) {
    const lineError = line && (document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-Line-item-error-${line}`));
    const target = lineError?.querySelector('.cart-item-error') || document.getElementById('cart-errors') || document.getElementById('cartDrawerErrors');
    if (target) {
      target.setAttribute('role', 'alert');
      target.textContent = message;
    }
  }

  updateLiveRegions(line, message = '') {
    this.showError(message, line);
  }

  getSectionInnerHTML(html, selector) {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }
}
customElements.define('cart-items', CartItemsUpdate);

if (!customElements.get('cart-note-box')) {
  customElements.define(
    'cart-note-box',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}


class ShippingCalculator extends HTMLElement {
  constructor() {
    super();
    this.section = this.dataset.section;
    this.setupCountries();
    this.zip = this.querySelector('[data-address-zip]');
    this.country = this.querySelector('[data-address-country-select]');
    this.province = this.querySelector('[data-address-province-select]');
    this.button = this.querySelector('button');
    this.errors = this.querySelector('#shippingErrors');
    this.success = this.querySelector('#shippingResponse');
    this.button.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector(`shippingCountry-${this.section}`, `shippingProvince-${this.section}`, {
        hideElement: `shipping-province-${this.section}`
      });
    }
  }
  onSubmitHandler(event) {
    event.preventDefault();
    this.errors.classList.add('hidden');
    this.success.classList.add('hidden');
    this.zip.classList.remove('invalid');
    this.country.classList.remove('invalid');
    this.province.classList.remove('invalid');
    this.button.classList.add('loading');
    this.button.setAttribute('disabled', true);

    const parameters = new URLSearchParams({
      'shipping_address[zip]': this.zip.value,
      'shipping_address[country]': this.country.value,
      'shipping_address[province]': this.province.value,
    });
    const sectionUrl = `${window.routes.cart_url.replace(/\/$/, '')}/shipping_rates.json?${parameters}`;

    fetch(sectionUrl, { method: 'GET', headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((result) => {
        if (result.shipping_rates) {
          if(result.shipping_rates.length > 0){
               this.success.classList.remove('hidden');
              this.success.innerHTML = '';
              result.shipping_rates.forEach((rate) => {
                const child = document.createElement('p');
                child.textContent = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
                this.success.appendChild(child);
          });
          }else{
               this.errors.classList.remove('hidden');
               this.errors.innerHTML = '';
              this.errors.textContent = 'No hay tarifas disponibles para este destino. Verifica los datos de envío.';
          }
       
        }
        else {
          this.errors.classList.remove('hidden');
          this.errors.textContent = result.zip ? 'Código postal no válido. Revisa el código postal para este destino.' : 'Revisa el país, la región y el código postal e inténtalo nuevamente.';
        }
      })
      .catch(() => {
        this.errors.classList.remove('hidden');
        this.errors.textContent = 'No pudimos consultar las tarifas. Verifica tu conexión e inténtalo nuevamente.';
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }
}

customElements.define('cart-shipping-box', ShippingCalculator);

